import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderStatusEnum } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { bimExportWorkOrderGql } from 'pages/Project/BimProject/WorkOrder/bimWorkOrderQueries';
import { gqlWorkOrderAsExcel } from 'pages/Project/UerProject/WorkOrder/workOrderQueries';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { downloadURI } from 'utils/downloadDataURI';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useProjectData } from './useProjectData';
import { useSendGpnAnalyticsEvent } from './useSendGpnAnalyticsEvent';

export const useWorkOrderDownload = ({
    workOrderId,
    instanceId,
    workOrderStatus,
    methodology,
}: {
    workOrderId: number;
    instanceId: string;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    methodology: ProjectMethodologyEnum | undefined;
}) => {
    const dispatch = useDispatch();
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const isBim = useMemo(() => methodology === ProjectMethodologyEnum.Bim, [methodology]);

    const [isSubmittedDownload, setIsSubmittedDownload] = useState<boolean>(false);

    const requestId = useMemo(() => `workOrderAsExcel_${workOrderId}_${instanceId}`, [instanceId, workOrderId]);

    const [workOrderAsExcelReset] = useResetStoreData({ requestId });

    const [
        uerWorkOrderAsExcelData,
        uerWorkOrderAsExcelStatus,
        uerWorkOrderAsExcelError,
        uerWorkOrderAsExcel,
        ,
        uerWorkOrderAsExcelAbort,
    ] = useRequest({
        requestId,
        query: gqlWorkOrderAsExcel,
        withEffect: false,
    });

    const [
        bimWorkOrderAsExcelData,
        bimWorkOrderAsExcelStatus,
        bimWorkOrderAsExcelError,
        bimWorkOrderAsExcel,
        ,
        bimWorkOrderAsExcelAbort,
    ] = useRequest({
        requestId,
        query: bimExportWorkOrderGql,
        withEffect: false,
    });

    useEffect(() => {
        const isUerSuccess =
            !isBim &&
            uerWorkOrderAsExcelStatus === RequestStatus.SUCCESS &&
            uerWorkOrderAsExcelData?.uerWorkOrderExportAsExcel?.data;
        const isBimSuccess =
            isBim &&
            bimWorkOrderAsExcelStatus === RequestStatus.SUCCESS &&
            bimWorkOrderAsExcelData?.bimExportWorkOrder?.data;

        if (isUerSuccess || isBimSuccess) {
            const { dataUrl, name } =
                (!isBim
                    ? uerWorkOrderAsExcelData?.uerWorkOrderExportAsExcel?.data
                    : bimWorkOrderAsExcelData?.bimExportWorkOrder?.data) || {};

            if (dataUrl && name) {
                downloadURI(dataUrl, name);
            }

            workOrderAsExcelReset(undefined);
        }
    }, [
        bimWorkOrderAsExcelData,
        bimWorkOrderAsExcelStatus,
        dispatch,
        isBim,
        uerWorkOrderAsExcelData,
        uerWorkOrderAsExcelStatus,
        workOrderAsExcelReset,
    ]);

    const handleDownloadWorkOrder = useCallback(() => {
        if (isBim) {
            dispatch(bimWorkOrderAsExcel({ workOrderId }));
        } else {
            dispatch(uerWorkOrderAsExcel({ workOrderId }));
        }

        sendGpnAnalyticsEvent({
            componentId: 'rim_order unload_pressed',
            projectId,
            contractId: projectData?.contractId,
            orderId: workOrderId,
            orderStatus: workOrderStatus,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        });

        setIsSubmittedDownload(true);
    }, [
        bimWorkOrderAsExcel,
        dispatch,
        isBim,
        projectData?.contractId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        uerWorkOrderAsExcel,
        workOrderId,
        workOrderStatus,
    ]);

    const handleCloseModalErrorDownload = useCallback(() => {
        setIsSubmittedDownload(false);
    }, []);

    const handleCreateReportDownload = useCallback(() => {
        ExportCSVReport(
            uerWorkOrderAsExcelData?.uerWorkOrderExportAsExcel?.validation ||
                bimWorkOrderAsExcelData?.bimExportWorkOrder?.validation,
        );
    }, [
        bimWorkOrderAsExcelData?.bimExportWorkOrder?.validation,
        uerWorkOrderAsExcelData?.uerWorkOrderExportAsExcel?.validation,
    ]);

    const workOrderAsExcelAbort = useCallback(() => {
        if (isBim) {
            dispatch(isBim ? bimWorkOrderAsExcelAbort(undefined) : uerWorkOrderAsExcelAbort(undefined));
        }
    }, [bimWorkOrderAsExcelAbort, dispatch, isBim, uerWorkOrderAsExcelAbort]);

    return {
        isSubmittedDownload,
        workOrderAsExcelData: isBim
            ? bimWorkOrderAsExcelData?.bimExportWorkOrder
            : uerWorkOrderAsExcelData?.uerWorkOrderExportAsExcel,
        workOrderAsExcelStatus: isBim ? bimWorkOrderAsExcelStatus : uerWorkOrderAsExcelStatus,
        workOrderAsExcelError: isBim ? bimWorkOrderAsExcelError : uerWorkOrderAsExcelError,
        workOrderAsExcelAbort,
        handleDownloadWorkOrder,
        handleCloseModalErrorDownload,
        handleCreateReportDownload,
    };
};
